import { useState } from 'react'
import { PublicLayout } from '@/components/layout/PublicLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { requestJson } from '@/lib/http'

type DocumentRecord = {
  id: string
  verification_status: string
  document_type: string
  case_id: string
  party_name: string
  rejection_reason?: string
  reviewed_at?: string
}

type Props = {
  documents: DocumentRecord[]
}

export default function PlatformIdentityVerificationsIndex({ documents }: Props) {
  const [reasonByDocument, setReasonByDocument] = useState<Record<string, string>>({})
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const action = async (documentId: string, reviewAction: 'approve' | 'reject') => {
    setMessage('')
    setError('')

    try {
      await requestJson(`/platform/business-registration/us/identity_verifications/${documentId}`, {
        method: 'PATCH',
        data: {
          action: reviewAction,
          reason: reasonByDocument[documentId],
        },
      })
      setMessage(`Document ${reviewAction}d`)
      window.location.reload()
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Request failed')
    }
  }

  return (
    <PublicLayout title="Identity Verifications" fullWidth>
      <div className="p-8 max-w-5xl mx-auto space-y-4">
        <h1 className="text-2xl font-semibold">Manual Identity Verification Queue</h1>
        {message && <p className="text-sm text-green-700">{message}</p>}
        {error && <p className="text-sm text-red-700">{error}</p>}

        {documents.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-sm text-muted-foreground">No queued identity documents.</CardContent>
          </Card>
        ) : (
          documents.map((document) => (
            <Card key={document.id}>
              <CardHeader>
                <CardTitle className="text-base">
                  {document.party_name} • {document.document_type} • {document.verification_status}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">Case: {document.case_id}</p>
                <Input
                  placeholder="Rejection reason (required for reject)"
                  value={reasonByDocument[document.id] || ''}
                  onChange={(event) => setReasonByDocument({ ...reasonByDocument, [document.id]: event.target.value })}
                />
                <div className="flex gap-2">
                  <Button onClick={() => action(document.id, 'approve')}>Approve</Button>
                  <Button variant="outline" onClick={() => action(document.id, 'reject')}>Reject</Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </PublicLayout>
  )
}
